<script>
import {
    eventGroupState,
    isSelectedEventGroupLocked
} from '@app/store/eventGroupStore.svelte.js';
import {
    projectState
} from '@app/store/projectStore.svelte.js';
import {
    commitHistory
} from '@app/store/historyStore.svelte.js';

import removeEvent from '@cclib/functions/eventGroups/removeEvent.js';
import convertToPlainTextCustom from '@cclib/functions/quill/convertToPlainTextCustom.js';
import convertToHtml from '@cclib/functions/quill/convertToHtml.js';
import { current } from 'immer';
import Swal from "sweetalert2";
import { SWAL_BASE, SWAL_CLASSES } from "@app/lib/swalClasses.js";
import { SvelteSet } from "svelte/reactivity";
import { toast } from "@zerodevx/svelte-toast";
import * as m from '@app/paraglide/messages.js';

let isDisabled = $derived(!eventGroupState[projectState.selected] || eventGroupState[projectState.selected].selected.length === 0 || isSelectedEventGroupLocked());

const mergeEvents = async function() {
    try {
        if (isSelectedEventGroupLocked()) {
            toast.push(m.event_group_locked_toast(), { classes: ["toast-warning"] });
            return;
        }

        if (eventGroupState[projectState.selected] && eventGroupState[projectState.selected].selected.length > 0) {
            //If there is more than one event selected, ask the user if they want to merge all of the selected events. If there is only one event selected, then ask the user if they wish to merge the selected event with the event before it or after it.
            if (eventGroupState[projectState.selected].selected.length > 1) {
                let swalRes = await Swal.fire({
                    ...SWAL_BASE,
                    customClass: SWAL_CLASSES.confirmCancel,
                    title: m.merge_swal_title(),
                    text: m.merge_swal_multiple_text(),
                    icon: "warning",
                    showCancelButton: true,
                    confirmButtonText: m.merge_swal_confirm(),
                    cancelButtonText: m.merge_swal_cancel(),
                });

                if (swalRes.isConfirmed) {
                    mergeSelectedEvents();
                } else {
                    return;
                }

            } else {
                let swalRes = await Swal.fire({
                    ...SWAL_BASE,
                    customClass: {
                        ...SWAL_CLASSES.denyCancel,
                        cancelButton: SWAL_CLASSES.confirmCancel.cancelButton,
                    },
                    title: m.merge_swal_title(),
                    text: m.merge_swal_single_text(),
                    showCancelButton: true,
                    showDenyButton: true,
                    confirmButtonText: m.merge_swal_before(),
                    denyButtonText: m.merge_swal_after(),
                    cancelButtonText: m.merge_swal_cancel_button()
                });

                if (swalRes.isConfirmed) {
                    if (eventGroupState[projectState.selected].selected[0] > 0) {
                        eventGroupState[projectState.selected].selected = [eventGroupState[projectState.selected].selected[0] - 1, eventGroupState[projectState.selected].selected[0]];
                    } else {
                        return;
                    }
                } else if (swalRes.isDenied) {
                    if (eventGroupState[projectState.selected].selected[0] < eventGroupState[projectState.selected].events.length - 1) {
                        eventGroupState[projectState.selected].selected = [eventGroupState[projectState.selected].selected[0], eventGroupState[projectState.selected].selected[0] + 1];
                    } else {
                        return;
                    }
                } else {
                    return;
                }

                mergeSelectedEvents();
            }
        }
    } catch (err) {
        console.log(err, err.message);

    }
};

const mergeSelectedEvents = async function() {
    try {
        const egIdx = projectState.selected;
        let sortedEvents = [...eventGroupState[egIdx].selected].sort((a, b) => {
            return b - a;
        });
        let lastEnd = eventGroupState[egIdx].events[sortedEvents[0]].end;

        commitHistory({
            name: "merge events",
            eventGroup: egIdx,
            mutate: {
                eventGroupState: (draft) => {
                    let text = "";
                    let originalText = "";
                    let speakers = [];

                    sortedEvents.forEach((eventIndex, selectedIndex) => {
                        text =
                            convertToPlainTextCustom(
                                draft[egIdx].events[eventIndex].text,
                            ) +
                            "\n" +
                            convertToPlainTextCustom(text);
                        originalText =
                            convertToPlainTextCustom(
                                draft[egIdx].events[eventIndex].original,
                            ) +
                            "\n" +
                            convertToPlainTextCustom(originalText);
                        if (draft[egIdx].events[eventIndex].speakers.length > 0) {
                            speakers = [
                                ...speakers,
                                ...draft[egIdx].events[eventIndex].speakers,
                            ];
                        }

                        if (selectedIndex < sortedEvents.length - 1) {
                            draft[egIdx] = removeEvent(
                                current(draft[egIdx]),
                                eventIndex,
                            );
                        }
                    });

                    const uniqueSpeakers = [];
                    const speakerIds = new SvelteSet();
                    speakers.forEach((speaker) => {
                        if (speaker && speaker.id && !speakerIds.has(speaker.id)) {
                            speakerIds.add(speaker.id);
                            uniqueSpeakers.push(speaker);
                        }
                    });

                    const keepIdx = sortedEvents[sortedEvents.length - 1];
                    const mergedEvent = draft[egIdx].events[keepIdx];
                    mergedEvent.text = convertToHtml(text);
                    mergedEvent.original = convertToHtml(originalText);
                    mergedEvent.end = lastEnd || mergedEvent.end;
                    mergedEvent.speakers = uniqueSpeakers;
                    mergedEvent.rendered = false;
                    draft[egIdx].selected = [];
                },
            },
        });

        const mergedEvent =
            eventGroupState[egIdx].events[sortedEvents[sortedEvents.length - 1]];
        if (window.quillEditor && mergedEvent) {
            let delta = window.quillEditor.clipboard.convert({
                html: mergedEvent.text
            });

            window.quillEditor.setContents(delta, "silent");
            window.quillEditor.formatLine(
                0,
                9999,
                "align",
                mergedEvent.alignment === "left" ? "" : mergedEvent.alignment,
                "silent"
            );
        }
    } catch (err) {
        console.log(err, err.message);
    }
};
</script>
<li class="nav-item" title={m.merge_title()}>
    <a 
        class="nav-link" 
        href="#!/" 
        id="merge-events-btn"
        role="button"
        aria-label={m.merge_aria_label()}
        aria-disabled={isDisabled}
        class:disabled={isDisabled}
        onclick={mergeEvents}
    ><i class="bi bi-view-list"></i><i class="bi bi-chevron-contract small"></i></a>
</li>